import { Injectable, inject, signal } from '@angular/core';
import { WINDOW } from '@innogy/utils-dom';
import { CookieService } from 'ngx-cookie-service';

// Verify if a new cookie name is actually needed before enabling on P.
export const CONSENT_COOKIE_NAME = 'consentLevel';

/**
 * Enum that represents the different levels of consent an end-user can give.
 * These consent levels are to be assigned to the consent cookie.
 */
export const CONSENT_LEVEL = {
  UNDEFINED: 0,
  DEFAULT: 1,
  TRACKING: 2,
} as const;

export const CONSENT_ACTION = {
  ACCEPT: 'accept-tracking',
  REJECT: 'reject-tracking',
} as const;

type CONSENT_ACTION_KEYS = keyof typeof CONSENT_ACTION;
export type CONSENT_ACTION_VALUES =
  (typeof CONSENT_ACTION)[CONSENT_ACTION_KEYS];

type CONSENT_LEVEL_KEYS = keyof typeof CONSENT_LEVEL;
export type CONSENT_LEVEL_VALUES = (typeof CONSENT_LEVEL)[CONSENT_LEVEL_KEYS];

@Injectable({
  providedIn: 'root',
})
export class ConsentService {
  readonly #cookieService = inject(CookieService);
  readonly #window = inject(WINDOW);
  readonly #consentGranted = signal(false);
  readonly consentStatus = this.#consentGranted.asReadonly();

  constructor() {
    if (this.consentGranted()) {
      this.#consentGranted.set(true);
    }
  }

  public consentGranted() {
    return this.getCookieConsentLevel() > CONSENT_LEVEL.UNDEFINED;
  }

  /**
   * Checks whether or not an end-user has opted in to allowing tracking cookies.
   * @returns boolean indicating whether or not tracking cookies are allowed on this device.
   */
  public allowsTrackingCookies() {
    return this.getCookieConsentLevel() === CONSENT_LEVEL.TRACKING;
  }

  /**
   * Maps a consent action to a consent level
   * @param action Consent action
   * @returns Corresponding consent level for the action
   */
  public consentLevelForAction(
    action: CONSENT_ACTION_VALUES
  ): CONSENT_LEVEL_VALUES {
    if (action === CONSENT_ACTION.ACCEPT) {
      return CONSENT_LEVEL.TRACKING;
    } else {
      return CONSENT_LEVEL.DEFAULT;
    }
  }

  /**
   * Get the cookie consent.
   *
   * @returns Consent, 0 if not existing
   */
  public getCookieConsentLevel(): CONSENT_LEVEL_VALUES {
    let cookieLevel = this.#cookieService.get(CONSENT_COOKIE_NAME);
    cookieLevel =
      cookieLevel.length > 0 ? cookieLevel : CONSENT_LEVEL.UNDEFINED.toString();
    const level = parseInt(cookieLevel, 10) as CONSENT_LEVEL_VALUES;
    return Number.isNaN(level) ? CONSENT_LEVEL.UNDEFINED : level;
  }

  public setCookieConsentLevel(level: number, durationInDays = 365) {
    this.#consentGranted.set(true);
    const domain = this.#getDomainName();
    const exdate = new Date();
    exdate.setDate(exdate.getDate() + durationInDays);
    this.#cookieService.set(CONSENT_COOKIE_NAME, level.toString(10), {
      path: '/',
      domain,
      expires: exdate,
      secure: !this.#isLocalhost(),
    });
  }

  #isLocalhost() {
    const { location } = this.#window;
    return ['localhost', '127.0.0.1'].includes(location.hostname);
  }

  #getDomainName() {
    const { location } = this.#window;
    if (this.#isLocalhost()) {
      return location.hostname;
    }
    return this.#getSubDomainName(location.host);
  }

  #getSubDomainName(host: string) {
    const domainParts = host.split('.');
    if (domainParts.length > 2) {
      domainParts.shift();
    }
    return `.${domainParts.join('.')}`;
  }
}
